<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="bs-backdrop fixed inset-0 bg-black/30 z-[999]"
      @click.self="close"
    >
      <transition name="sheet-slide">
        <div
          v-if="modelValue"
          class="bs-sheet fixed bottom-0 left-0 w-full bg-white shadow-2xl rounded-t-2xl flex flex-col"
          :style="{ height: sheetHeight + 'px', transition: resizing ? 'none' : 'height 0.25s ease' }"
          ref="sheet"
        >
          <!-- draggable handle -->
          <div
            class="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-2 cursor-row-resize"
            @mousedown.prevent="startDrag"
            @touchstart.prevent="startDragTouch"
          ></div>

          <!-- content -->
          <div class="sheet-content flex-1 overflow-y-auto p-4">
            <slot />
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, required: true }
})
const emit = defineEmits(['update:modelValue'])

const sheet = ref(null)
const sheetHeight = ref(window.innerHeight * 0.5) // initial 50% viewport
const resizing = ref(false)

let startY = 0
let startHeight = 0

// ---------------------- Desktop drag ----------------------
function startDrag(e) {
  startY = e.clientY
  startHeight = sheetHeight.value
  resizing.value = true
  window.addEventListener('mousemove', onDrag)
  window.addEventListener('mouseup', stopDrag)
}

function onDrag(e) {
  const dy = startY - e.clientY
  let newHeight = startHeight + dy
  const maxHeight = window.innerHeight * 0.9
  const minHeight = 100
  sheetHeight.value = Math.min(Math.max(newHeight, minHeight), maxHeight)
}

function stopDrag() {
  resizing.value = false
  window.removeEventListener('mousemove', onDrag)
  window.removeEventListener('mouseup', stopDrag)
}

// ---------------------- Touch support ----------------------
function startDragTouch(e) {
  if (e.touches.length !== 1) return
  startY = e.touches[0].clientY
  startHeight = sheetHeight.value
  resizing.value = true
  window.addEventListener('touchmove', onDragTouch)
  window.addEventListener('touchend', stopDragTouch)
}

function onDragTouch(e) {
  if (e.touches.length !== 1) return
  const dy = startY - e.touches[0].clientY
  const maxHeight = window.innerHeight * 0.9
  const minHeight = 100
  let newHeight = startHeight + dy
  sheetHeight.value = Math.min(Math.max(newHeight, minHeight), maxHeight)
}

function stopDragTouch() {
  resizing.value = false
  window.removeEventListener('touchmove', onDragTouch)
  window.removeEventListener('touchend', stopDragTouch)
}

// ---------------------- Close sheet ----------------------
function close() {
  emit('update:modelValue', false)
}

// Cleanup
onUnmounted(() => {
  window.removeEventListener('mousemove', onDrag)
  window.removeEventListener('mouseup', stopDrag)
  window.removeEventListener('touchmove', onDragTouch)
  window.removeEventListener('touchend', stopDragTouch)
})
</script>

<style scoped>
.bs-backdrop {
  display: flex;
  justify-content: center;
  align-items: end; /* anchor sheet to bottom */
}

.bs-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;        /* ensures full width */
  width: 100%;     /* always full width */
  background: white;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 9999;
}

/* handle */
.bs-sheet > div.cursor-row-resize {
  user-select: none;
}

/* transition for slide-in/out */
.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateY(100%);
}
.sheet-slide-enter-to,
.sheet-slide-leave-from {
  transform: translateY(0%);
}
.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: transform 0.3s ease;
}
</style>
